import React from "react"

import {
  customerRepositoryCreate,
  getCustomerRepositoryListQueryKey,
} from "@boundless/bl-api-client/customer/services"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { DocumentSelectionBox } from "@/components/documents/DocumentSelectionBox"
import {
  uploadQueueReducer,
  type UploadEntry,
} from "@/components/documents/reducers/uploadQueueReducer"
import { UploadProgressBar } from "@/components/documents/UploadProgressList"
import { FormHeader } from "@/components/form-elements/FormHeader"
import { Card, CardContent } from "@/components/ui/card"
import type { ErrorType } from "@/lib/clients"

interface Props {
  companyUuid: string
}

export function UploadDocumentCard({ companyUuid }: Props) {
  const queryClient = useQueryClient()
  const [queue, dispatch] = React.useReducer(uploadQueueReducer, [])

  const sendUploadRequest = (entry: UploadEntry) => {
    return customerRepositoryCreate(
      companyUuid,
      { file: entry.file },
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          const progress = Math.round((event.loaded * 100) / (event.total || 1))
          dispatch({ type: "PROGRESS", uuid: entry.uuid, progress })
        },
      },
    )
  }

  const { mutate: uploadFile } = useMutation<
    void,
    ErrorType<{ file: string[] }>,
    UploadEntry
  >({
    mutationFn: sendUploadRequest,
    onSuccess: (_, entry) => {
      dispatch({ type: "REMOVE", uuid: entry.uuid })

      const queryKey = getCustomerRepositoryListQueryKey(companyUuid)
      queryClient.invalidateQueries({ queryKey: queryKey })
    },
    onError: (error, entry) => {
      const message = error.response?.data?.file?.[0] ?? error.message
      dispatch({ type: "ERROR", uuid: entry.uuid, message })
    },
  })

  const handleSelect = (files: FileList) => {
    const newUploads: UploadEntry[] = Array.from(files).map((file) => ({
      uuid: crypto.randomUUID(),
      file,
      progress: 0,
      error: null,
    }))
    dispatch({ type: "ADD", entries: newUploads })
    newUploads.map((entry) => uploadFile(entry))
  }

  const handleDismiss = (uuid: string) => {
    dispatch({ type: "REMOVE", uuid: uuid })
  }

  return (
    <Card className="w-full border-0 bg-gray-50 py-0 shadow-sm">
      <CardContent className="p-4 sm:p-6 lg:p-6">
        <div className="flex w-full flex-col">
          <FormHeader title="Upload Document" />

          <div className="w-full">
            {queue.length ? (
              <UploadProgressBar queue={queue} onDismiss={handleDismiss} />
            ) : (
              <div className="w-full">
                <DocumentSelectionBox onSelect={handleSelect} />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
